import React, { useState } from 'react';
import {
    Container, Typography, Box, Button, Card, CardContent, CardActions,
    Chip, Grid, Dialog, DialogContent, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import { Download, Close, Visibility, OpenInNew, PictureAsPdf, Image, Language } from '@mui/icons-material';
import PageWrapper from '../components/PageWrapper';

const typeConfig = {
    document: { icon: PictureAsPdf, color: '#c62828', label: 'Document' },
    image:    { icon: Image,        color: '#1565c0', label: 'Image' },
    website:  { icon: Language,     color: '#2e7d32', label: 'Website' },
};

const Resources = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [viewOpen, setViewOpen] = useState(false);
    const [viewResource, setViewResource] = useState(null);

    const handleView = (resource) => {
        setViewResource(resource);
        setViewOpen(true);
    };

    const handleCloseView = () => {
        setViewOpen(false);
        setViewResource(null);
    };

    const handleDownload = (url, filename) => {
        const link = document.createElement('a');
        link.href = `${process.env.PUBLIC_URL}${url}`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleVisit = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const renderActions = (resource) => {
        switch (resource.type) {
            case 'website':
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<OpenInNew />}
                        onClick={() => handleVisit(resource.url)}
                        sx={{ borderRadius: 2 }}
                    >
                        Visit
                    </Button>
                );
            case 'document':
            case 'image':
                return (
                    <>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Visibility />}
                            onClick={() => handleView(resource)}
                            sx={{ borderRadius: 2 }}
                        >
                            View
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<Download />}
                            onClick={() => handleDownload(resource.downloadUrl, resource.downloadFilename)}
                            sx={{ borderRadius: 2 }}
                        >
                            Download
                        </Button>
                    </>
                );
            default:
                return null;
        }
    };

    const handleIframeLoad = (e) => {
        try {
            const iframeDoc = e.target.contentDocument || e.target.contentWindow.document;
            iframeDoc.querySelectorAll('a').forEach((link) => {
                link.setAttribute('target', '_blank');
                link.setAttribute('rel', 'noopener noreferrer');
            });
        } catch {
            // Cross-origin iframe — can't modify link targets
        }
    };

    const renderViewerContent = () => {
        if (!viewResource) return null;

        if (viewResource.type === 'document') {
            return (
                <Box
                    component="iframe"
                    src={`${process.env.PUBLIC_URL}${viewResource.viewUrl}`}
                    title={viewResource.title}
                    onLoad={handleIframeLoad}
                    sx={{
                        width: isMobile ? '95vw' : '85vw',
                        height: '90vh',
                        border: 'none',
                        borderRadius: 1,
                        backgroundColor: '#fff',
                    }}
                />
            );
        }

        return (
            <Box
                component="img"
                src={`${process.env.PUBLIC_URL}${viewResource.viewUrl}`}
                alt={viewResource.title}
                sx={{
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                    borderRadius: 1,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }}
            />
        );
    };

    return (
        <PageWrapper configKey="resources" pageTitle="Family Resources">
            {(config) => {
                const resources = config.resources || [];
                const pageTitle = config.pageTitle || 'Resources';
                const pageDescription = config.pageDescription || '';

                return (
                    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
                        <Box sx={{ textAlign: 'center', mb: 5 }}>
                            <Typography
                                variant="h3"
                                component="h2"
                                gutterBottom
                                sx={{ textAlign: { xs: 'left', sm: 'center' } }}
                            >
                                {pageTitle}
                            </Typography>
                            {pageDescription && (
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    sx={{ maxWidth: 600, mx: 'auto', fontWeight: 400 }}
                                >
                                    {pageDescription}
                                </Typography>
                            )}
                        </Box>

                        <Grid container spacing={3} justifyContent={resources.length < 3 ? 'center' : 'flex-start'}>
                            {resources.map((resource, index) => {
                                const tConfig = typeConfig[resource.type] || typeConfig.website;
                                const TypeIcon = tConfig.icon;

                                return (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index} sx={{ display: 'flex' }}>
                                        <Card
                                            elevation={0}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderRadius: 3,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                background: 'linear-gradient(145deg, #f8faff 0%, #f3f6fd 100%)',
                                                boxShadow: '0 2px 8px rgba(37, 61, 141, 0.08)',
                                                transition: 'all 0.25s ease',
                                                '&:hover': {
                                                    borderColor: 'primary.light',
                                                    background: 'linear-gradient(145deg, #f0f5ff 0%, #eaf0fb 100%)',
                                                    boxShadow: '0 6px 24px rgba(37, 61, 141, 0.16)',
                                                    transform: 'translateY(-2px)',
                                                },
                                            }}
                                        >
                                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <TypeIcon sx={{ fontSize: 20, color: tConfig.color }} />
                                                    <Chip
                                                        label={resource.category || tConfig.label}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                </Box>
                                                <Typography
                                                    variant="h6"
                                                    component="h3"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 1.5,
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {resource.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ lineHeight: 1.6 }}
                                                >
                                                    {resource.description}
                                                </Typography>
                                            </CardContent>
                                            <CardActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
                                                {renderActions(resource)}
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        <Dialog
                            open={viewOpen}
                            onClose={handleCloseView}
                            maxWidth={false}
                            fullScreen={isMobile}
                            PaperProps={{
                                sx: {
                                    backgroundColor: viewResource?.type === 'document' ? '#525659' : 'transparent',
                                    boxShadow: viewResource?.type === 'document' ? undefined : 'none',
                                    borderRadius: isMobile ? 0 : 2,
                                    overflow: 'hidden',
                                },
                            }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <IconButton
                                    onClick={handleCloseView}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        zIndex: 1,
                                    }}
                                >
                                    <Close />
                                </IconButton>
                                <DialogContent sx={{ p: viewResource?.type === 'document' ? 0 : 1, overflow: 'hidden' }}>
                                    {renderViewerContent()}
                                </DialogContent>
                            </Box>
                        </Dialog>
                    </Container>
                );
            }}
        </PageWrapper>
    );
};

export default Resources;
